import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTL, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export function TransactionsPage({ kind }) {
  const { isAdmin } = useAuth();
  // kind = "income" or "expense"
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState({ banks: [], cash: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "", amount: "", account_id: "", account_type: "cash", note: "", due_date: "" });

  const isIncome = kind === "income";
  const CATS = isIncome ? ["Faiz", "Hizmet", "Komisyon", "Diğer"] : ["Kira", "Maaş", "Elektrik", "Su", "İnternet", "Vergi", "Reklam", "Nakliye", "Yakıt", "Ofis", "Mal alımı", "Banka masrafı", "Diğer"];
  const path = isIncome ? "/income" : "/expenses";

  const load = async () => setItems((await api.get(path)).data);
  useEffect(() => { load(); api.get("/accounts").then((r) => setAccounts(r.data)); }, [kind]);
  const allAccounts = [...accounts.banks.map((b) => ({ ...b, type: "bank" })), ...accounts.cash.map((c) => ({ ...c, type: "cash" }))];

  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.category || !Number(form.amount) || !form.account_id) return toast.error("Alanları doldurun");
    const body = { ...form, amount: Number(form.amount) };
    if (!isIncome) body.due_date = form.due_date || null;
    else delete body.due_date;
    setSaving(true);
    try {
      await api.post(path, body);
      toast.success("Başarıyla kaydedildi"); setOpen(false); load();
      setForm({ category: "", amount: "", account_id: "", account_type: "cash", note: "", due_date: "" });
    } catch (e) { if (e.response && e.response.status < 500) toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`${path}/${id}`); load(); };

  const total = items.reduce((s, x) => s + x.amount, 0);

  return (
    <div className="space-y-6" data-testid={`${kind}-page`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{isIncome ? "Gelirler" : "Giderler"}</h1>
          <p className="text-sm text-muted-foreground">Toplam: <span className={`num font-semibold ${isIncome ? "text-emerald-500" : "text-primary"}`}>{fmtTL(total)}</span></p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button><Plus size={16} className="mr-1" /> Yeni {isIncome ? "Gelir" : "Gider"}</Button></DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle>Yeni {isIncome ? "Gelir" : "Gider"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent className="bg-popover">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tutar</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div><Label>Hesap</Label>
                <Select value={form.account_id} onValueChange={(v) => { const a = allAccounts.find(z => z.id === v); setForm({ ...form, account_id: v, account_type: a?.type || "cash" }); }}>
                  <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent className="bg-popover">{allAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!isIncome && <div><Label>Vade Tarihi (opsiyonel)</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>}
              <div><Label>Not</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="txn-submit-btn">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tarih</TableHead><TableHead>Kategori</TableHead>
            {!isIncome && <TableHead>Vade</TableHead>}
            <TableHead>Not</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((x) => (
              <TableRow key={x.id}>
                <TableCell>{fmtDate(x.date)}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-muted">{x.category}</span></TableCell>
                {!isIncome && <TableCell>{x.due_date ? fmtDate(x.due_date) : "-"}</TableCell>}
                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{x.note}</TableCell>
                <TableCell className={`text-right num font-semibold ${isIncome ? "text-emerald-500" : "text-primary"}`}>{fmtTL(x.amount)}</TableCell>
                <TableCell>{isAdmin && <Button variant="ghost" size="icon" onClick={() => del(x.id)} data-testid={`txn-delete-${x.id}`}><Trash2 size={14} /></Button>}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={isIncome ? 5 : 6} className="text-center py-8 text-muted-foreground">Kayıt yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
